<!--
  A real <header> with exactly one real <h1>. No page in the app has one
  today (refresh_plan.md 3.1.6). Type treatment follows the prototype's
  `.doc__head`: a caps plate-face title on the chassis surface.

  ONE LINE, ONE HEIGHT (Mark, 2026-11-03). Everything the bar carries sits on
  the title's line, so every page's bar is the same height as the rail's user
  block beside it:

    back      a yellow double chevron to the page this one belongs under. It replaced
              a breadcrumb line ("Customers › Locations") that looked like a
              link and was plain text. It goes to the parent ROUTE, never to
              history.back(): these pages are opened from emails and pasted
              links, and "back" there leaves the app.
    subtitle  a short fact about the record on screen ("Acme Trucking",
              "Truck · 2019 · Freightliner"), after the title in lighter type.
              A sentence explaining the page does not belong here; it goes in
              the page under the bar (SectionLayout's intro line).
    meta      chips about the record (archived, a load's status), after that.
    actions   the page's buttons, at the far end.
-->
<template>
  <header class="lw-c-page-header">
    <div class="lw-c-page-header__row">
      <div class="lw-c-page-header__heading-group">
        <router-link
          v-if="back"
          :to="back.to"
          class="lw-c-page-header__back"
          :aria-label="`Back to ${back.label}`"
          :title="`Back to ${back.label}`"
          data-test="page-header-back"
        >
          <!--
            Two solid chevrons, the curve-warning sign at the roadside
            (Mark, 2026-11-03): drawn rather than taken from the icon font,
            whose double angle is thin, touching, and the rail's Collapse
            glyph. Each chevron is 5 units thick with a 5-unit gap, so the
            pair reads as one sign at 22px.
          -->
          <svg
            class="lw-c-page-header__back-mark"
            viewBox="0 0 25 22"
            width="25"
            height="22"
            aria-hidden="true"
            focusable="false"
          >
            <polygon points="10,0 0,11 10,22 15,22 5,11 15,0" />
            <polygon points="20,0 10,11 20,22 25,22 15,11 25,0" />
          </svg>
        </router-link>
        <h1 class="lw-c-page-header__title">
          {{ title }}
        </h1>
        <p v-if="subtitle" class="lw-c-page-header__subtitle">
          {{ subtitle }}
        </p>
        <div v-if="$slots.meta" class="lw-c-page-header__meta">
          <slot name="meta" />
        </div>
      </div>
      <div v-if="$slots.actions" class="lw-c-page-header__actions">
        <slot name="actions" />
      </div>
    </div>
  </header>
</template>

<script>
export default {
  name: 'LwPageHeader',
  props: {
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      default: null,
    },
    // { to, label }: `to` is any router-link location, `label` names the
    // parent for the chevron's accessible name and tooltip ("Back to
    // Customers").
    back: {
      type: Object,
      default: null,
      validator: value => value === null || (value.to != null && typeof value.label === 'string'),
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../../sass/mixins.generated';

// FULL BLEED, NO RULE (Mark, 2026-11-03). The bar sits flush under #header
// and runs to both edges of the page area, so it meets the rail rather than
// floating as a card on the canvas grey. The amber rule that closed it is
// gone: the road marking above is the shell's one yellow line.
//
// THE TOP is a negative margin through #content's gutter. THE SIDES are
// paint, not box: many pages cap their width and centre it (Dispatch, every
// create form), and the header is inside that cap, so no margin could reach
// the edges. A spread shadow in the bar's colour, clipped to the bar's own
// top and bottom, paints the band sideways, and #content's overflow clips it
// at the page area's edges. It adds no scroll width, and the title stays
// lined up with the page's content beneath it.
//
// STICKY (Mark, same day): the page scrolls under the bar, which stays
// pinned to the top of #content, the scroll container. A sticky box sticks
// inside its scroller's PADDING, so `top: 0` would hold it a gutter below the
// edge; the negative gutter pins it where the margin already puts it, flush
// under #header, and it does not move until the page does.
// The painted sides are part of the bar, so they cover what scrolls beneath
// them too.
//
// The height is shared with #nav-user (app.scss), and the content is centred
// in it so the bar and the rail's user block line up. Outside the shell (a
// unit test) the variables are unset and the bar sits where it is put.
.lw-c-page-header {
  background: var(--lw-bg-chassis);
  color: var(--lw-fg-inverse);
  box-shadow: 0 0 0 100vmax var(--lw-bg-chassis);
  box-sizing: border-box;
  clip-path: inset(0 -100vmax);
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: calc(-1 * var(--lw-c-shell-gutter, 0px));
  min-height: var(--lw-c-shell-band-height, 0);
  padding: var(--lw-space-3) var(--lw-space-5);
  position: sticky;
  top: calc(-1 * var(--lw-c-shell-gutter, 0px));
  z-index: var(--lw-z-sticky);
}

// When the title's line and the buttons do not fit side by side, the buttons
// go under the title rather than squeezing it: a load's three packet buttons
// once crushed "Coast Range Cellars" into a column one word wide, a 254px bar
// that, sticky, held half the window.
.lw-c-page-header__row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--lw-space-2) var(--lw-space-4);
}

// The title's line. It wraps only when the screen is too narrow to hold it;
// on a desk it is one line.
.lw-c-page-header__heading-group {
  align-items: baseline;
  display: flex;
  flex: 1 1 auto;
  flex-wrap: wrap;
  column-gap: var(--lw-space-3);
  row-gap: var(--lw-space-1);
  min-width: 0;
}

// The amber of the road marking above, the one place the bar spends it:
// the way out of this page. The target is the title line's height with an
// inset round the sign, pulled left by that inset so the sign, not the box,
// lines up with the page's content edge. The sign then sits 12px from the
// title, a little over twice its own gap, so it reads as one mark before it.
.lw-c-page-header__back {
  align-items: center;
  align-self: center;
  border-radius: var(--lw-radius-md);
  color: var(--lw-line-chassis);
  display: inline-flex;
  flex: none;
  height: 2rem;
  justify-content: center;
  margin-left: calc(-1 * var(--lw-space-2));
  margin-right: calc(-1 * var(--lw-space-2));
  padding: 0 var(--lw-space-2);
  text-decoration: none;
  transition: background-color var(--lw-motion-fast) var(--lw-motion-ease);
}

.lw-c-page-header__back-mark {
  display: block;
  fill: currentColor;
}

.lw-c-page-header__back:hover {
  background-color: var(--lw-navy-950);
  color: var(--lw-line-chassis);
}

.lw-c-page-header__back:focus-visible {
  outline: var(--lw-focus-ring-width) solid var(--lw-focus-ring-color);
  outline-offset: var(--lw-focus-ring-offset);
}

.lw-c-page-header__title {
  margin: 0;
  font: var(--lw-type-title);
  color: var(--lw-fg-inverse);
}

.lw-c-page-header__subtitle {
  margin: 0;
  font: var(--lw-type-body-sm);
  color: var(--lw-fg-inverse);
  opacity: 0.75;
}

.lw-c-page-header__meta {
  align-items: center;
  align-self: center;
  display: flex;
  gap: var(--lw-space-2);
}

.lw-c-page-header__actions {
  flex: 0 1 auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--lw-space-2);
}
</style>
